export const DEFAULT_SITE_URL = 'https://fuelcyclepro.com';
export const DEFAULT_BRAND_NAME = 'FuelCyclePro';
export const DEFAULT_OG_IMAGE_PATH = '/og-image.png';
export const DEFAULT_DESCRIPTION =
  'Fuel every ride with cycling training plans, ride fueling guidance, consistent execution habits, bonk prevention, and one-tap export to Zwift and Garmin.';

/** Keep paths aligned with `public/sitemap.xml` and `apps/web` `indexableRoutes`. */
export const INDEXABLE_PUBLIC_ROUTES = Object.freeze([
  {
    path: '/',
    title: 'Cycling nutrition and training plans | FuelCyclePro',
    description:
      'FuelCyclePro helps cyclists plan training, fuel every ride, build consistent execution habits, reduce bonk risk, and export workouts to Zwift and Garmin.',
    priority: '1.0',
  },
  {
    path: '/pricing',
    title: 'Cycling app pricing | FuelCyclePro',
    description:
      'See FuelCyclePro pricing for cyclists who want training plans, ride fueling guidance, bonk prevention, and workout export in one app.',
    priority: '0.9',
  },
  {
    path: '/eroica',
    title: "L'Eroica training and fueling | FuelCyclePro",
    description:
      "Under-trained or under-fueled for L'Eroica? Plans for Saturday L'Eroico and Crete Senesi, and Sunday Val d'Arbia, Gallo Nero, and Valle del Chianti - 3-4 October 2026.",
    priority: '0.8',
  },
  {
    path: '/install',
    title: 'Install FuelCyclePro | iOS and Android',
    description:
      'Add FuelCyclePro to your iPhone or Android Home Screen. Safari and Chrome steps so the icon opens as a web app.',
    priority: '0.8',
  },
  {
    path: '/about',
    title: 'About FuelCyclePro',
    description:
      'Learn how FuelCyclePro connects training plans, per-ride fueling, and consistent execution habits for stronger ride outcomes.',
    priority: '0.7',
  },
  {
    path: '/intervals',
    title: 'Intervals.icu setup | FuelCyclePro',
    description:
      'Connect Intervals.icu on the Free plan. Setup steps, device links, and connection terms for ride sync and calendar push.',
    priority: '0.6',
  },
  {
    path: '/beta',
    title: 'Get the apps | FuelCyclePro',
    description:
      'The iPhone and Android apps are coming soon. Use the website now.',
    priority: '0.8',
  },
  {
    path: '/contact',
    title: 'Contact FuelCyclePro',
    description:
      'Contact FuelCyclePro for support, partnerships, product questions, or press enquiries about training and cycling nutrition.',
    priority: '0.7',
  },
  {
    path: '/terms',
    title: 'Terms of service | FuelCyclePro',
    description:
      'Read the FuelCyclePro terms of service for subscriptions, account use, payments, data handling, and platform limitations.',
    priority: '0.3',
  },
  {
    path: '/privacy',
    title: 'Privacy policy | FuelCyclePro',
    description:
      'Read the FuelCyclePro privacy policy covering account data, training data, connected platforms, retention, and your GDPR rights.',
    priority: '0.3',
  },
]);

const AUTH_ROUTE_MATCHERS = Object.freeze([
  {
    matches: (pathname) => pathname === '/login',
    title: 'Sign in | FuelCyclePro',
    description: 'Sign in to FuelCyclePro to access your cycling plans, fueling workflow, and ride history.',
  },
  {
    matches: (pathname) => pathname === '/register',
    title: 'Create an account | FuelCyclePro',
    description:
      'Create a FuelCyclePro account to build cycling training plans, get ride fueling guidance, and export workouts.',
  },
  {
    matches: (pathname) => pathname === '/forgot-password',
    title: 'Reset your password | FuelCyclePro',
    description: 'Request a FuelCyclePro password reset email.',
  },
  {
    matches: (pathname) => pathname.startsWith('/reset-password/'),
    title: 'Choose a new password | FuelCyclePro',
    description: 'Choose a new FuelCyclePro password to regain access to your account.',
  },
  {
    matches: (pathname) =>
      pathname.startsWith('/verify-email/') ||
      pathname.startsWith('/app/verify-email/'),
    title: 'Verify your email | FuelCyclePro',
    description: 'Verify your email address to finish setting up your FuelCyclePro account.',
  },
  {
    matches: (pathname) => pathname === '/recovery',
    title: 'Recovery | FuelCyclePro',
    description:
      'Log sleep, see readiness, and get recovery actions tied to your training day and fueling.',
  },
]);

export function normalizeSiteUrl(rawSiteUrl = DEFAULT_SITE_URL) {
  const candidate = String(rawSiteUrl || DEFAULT_SITE_URL).trim();
  if (!candidate) return DEFAULT_SITE_URL;
  return candidate.endsWith('/') ? candidate.slice(0, -1) : candidate;
}

export function normalizePathname(pathname = '/') {
  if (!pathname || pathname === '/') return '/';
  const normalized = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
}

export function buildAbsoluteUrl(siteUrl, pathname = '/') {
  const normalizedSiteUrl = normalizeSiteUrl(siteUrl);
  const normalizedPathname = normalizePathname(pathname);
  return normalizedPathname === '/'
    ? `${normalizedSiteUrl}/`
    : `${normalizedSiteUrl}${normalizedPathname}`;
}

export function getIndexableRoute(pathname) {
  const normalizedPathname = normalizePathname(pathname);
  return INDEXABLE_PUBLIC_ROUTES.find((route) => route.path === normalizedPathname) ?? null;
}

function getAuthRoute(pathname) {
  const normalizedPathname = normalizePathname(pathname);
  return AUTH_ROUTE_MATCHERS.find((route) => route.matches(normalizedPathname)) ?? null;
}

function buildHomeStructuredData(siteUrl) {
  const homepageUrl = buildAbsoluteUrl(siteUrl, '/');
  const logoUrl = `${normalizeSiteUrl(siteUrl)}/apple-touch-icon.png`;

  return JSON.stringify(
    [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: DEFAULT_BRAND_NAME,
        url: homepageUrl,
        logo: logoUrl,
        description:
          'FuelCyclePro helps cyclists connect training plans, per-ride fueling, bonk prevention, and workout export.',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: DEFAULT_BRAND_NAME,
        url: homepageUrl,
        description:
          'Cycling training plans, ride fueling guidance, bonk prevention, and workout export.',
      },
    ],
    null,
    2
  );
}

export function getSeoForPath(pathname, siteUrl = DEFAULT_SITE_URL) {
  const normalizedPathname = normalizePathname(pathname);
  const normalizedSiteUrl = normalizeSiteUrl(siteUrl);
  const indexableRoute = getIndexableRoute(normalizedPathname);

  if (indexableRoute) {
    return {
      ...indexableRoute,
      pathname: normalizedPathname,
      canonicalUrl: buildAbsoluteUrl(normalizedSiteUrl, normalizedPathname),
      robots: 'index,follow',
      ogImageUrl: `${normalizedSiteUrl}${DEFAULT_OG_IMAGE_PATH}`,
      structuredData:
        normalizedPathname === '/' ? buildHomeStructuredData(normalizedSiteUrl) : null,
    };
  }

  const authRoute = getAuthRoute(normalizedPathname);
  if (authRoute) {
    return {
      ...authRoute,
      pathname: normalizedPathname,
      canonicalUrl: buildAbsoluteUrl(normalizedSiteUrl, normalizedPathname),
      robots: 'noindex,nofollow',
      ogImageUrl: `${normalizedSiteUrl}${DEFAULT_OG_IMAGE_PATH}`,
      structuredData: null,
    };
  }

  return {
    title: null,
    description: DEFAULT_DESCRIPTION,
    pathname: normalizedPathname,
    canonicalUrl: buildAbsoluteUrl(normalizedSiteUrl, normalizedPathname),
    robots: 'noindex,nofollow',
    ogImageUrl: `${normalizedSiteUrl}${DEFAULT_OG_IMAGE_PATH}`,
    structuredData: null,
  };
}

export function getSitemapEntries(siteUrl = DEFAULT_SITE_URL) {
  return INDEXABLE_PUBLIC_ROUTES.map((route) => ({
    loc: buildAbsoluteUrl(siteUrl, route.path),
    priority: route.priority,
  }));
}
